/**
 * View-model hook for transcript deep links (`?event=` / `?message=`).
 *
 * Resolves the deep-link target against the timeline and drives the
 * selection side effects needed to make it visible:
 * - message ID → event resolution (selected span first, then root/branches)
 * - cross-timeline switch when the target lives in another root timeline
 * - swimlane row selection when the target is outside the visible rows
 *
 * Each side effect fires once per deep-link key change, guarded by refs —
 * see the comments on each effect for the races those guards prevent.
 */

import { useEffect, useMemo, useRef } from "react";

import type { Event } from "@tsmono/inspect-common/types";

import {
  findTimelineIndexForEvent,
  findTimelineIndexForMessage,
  timelineContainsEvent,
} from "../findTimelineForDeepLink";
import {
  resolveEventInBranches,
  resolveEventToSpan,
  resolveMessageInBranches,
  resolveMessageToEvent,
} from "../resolveMessageToEvent";
import { type TranscriptTimelineResult } from "../timeline/hooks";
import {
  getSelectedSpans,
  type SpanSelectKey,
} from "../timeline/timelineEventNodes";

export interface UseDeepLinkResolutionOptions {
  /** Deep-link to a specific event. Takes priority over initialMessageId. */
  initialEventId?: string | null;
  /** Deep-link to a message ID, resolved to the best matching event. */
  initialMessageId?: string | null;
  /** Full timeline pipeline result (rows, selection, timelines, root). */
  timeline: TranscriptTimelineResult;
  /** Span ID → swimlane selection key lookup. */
  spanSelectKeys: ReadonlyMap<string, SpanSelectKey>;
  /** Whether swimlanes (and therefore row-scoped event lists) are active. */
  showSwimlanes: boolean;
  /** The events currently in the node feed (to detect visible targets). */
  nodeFeedEvents: Event[];
  /** Whether utility agents are currently shown. */
  includeUtility: boolean;
  /** Force the utility-agents toggle on (same effect as the header button). */
  setIncludeUtility: (include: boolean) => void;
  /** Headroom anchor reset, fired (debounced) whenever the effective scroll
   *  target changes — see the effect below for why. */
  onHeadroomResetAnchor?: (debounce?: boolean) => void;
}

export interface DeepLinkResolution {
  /** The event to scroll to: the explicit event id, the resolved message
   *  target, or the branch scroll target. Null when there is none. */
  effectiveInitialEventId: string | null;
}

export function useDeepLinkResolution(
  options: UseDeepLinkResolutionOptions
): DeepLinkResolution {
  const {
    initialEventId,
    initialMessageId,
    timeline: {
      timeline: timelineData,
      state: timelineState,
      multiTimeline: {
        timelines,
        activeIndex: activeTimelineIndex,
        setActive: setActiveTimeline,
      },
      selection: { branchScrollTarget },
    },
    spanSelectKeys,
    showSwimlanes,
    nodeFeedEvents,
    onHeadroomResetAnchor,
    includeUtility,
    setIncludeUtility,
  } = options;

  // ---------------------------------------------------------------------------
  // Message ID → event resolution
  // ---------------------------------------------------------------------------

  // Resolve message ID against the selected span first, then fall back to root.
  const resolvedLocal = useMemo(() => {
    if (initialEventId || !initialMessageId) return undefined;
    const selectedSpans = getSelectedSpans(
      timelineState.rows,
      timelineState.selected
    );
    for (const span of selectedSpans) {
      const result = resolveMessageToEvent(initialMessageId, span);
      if (result && !result.agentSpanId) return result;
    }
    return undefined;
  }, [
    initialEventId,
    initialMessageId,
    timelineState.rows,
    timelineState.selected,
  ]);

  const resolvedRoot = useMemo(() => {
    if (initialEventId || !initialMessageId || resolvedLocal) return undefined;
    // First try the main content tree (existing behavior).
    const main = resolveMessageToEvent(initialMessageId, timelineData.root);
    if (main) return main;
    // Fall back to branches: walk every branch in the tree and return the
    // first match in swimlane row order (latest fork first).
    return resolveMessageInBranches(initialMessageId, timelineData.root);
  }, [initialEventId, initialMessageId, resolvedLocal, timelineData.root]);

  const resolved = resolvedLocal ?? resolvedRoot;

  // Cross-timeline deep links: if the target lives in a different root
  // timeline, find it so the effect below can switch to it. -1 = no switch.
  const deepLinkTimelineIndex = useMemo(() => {
    if (timelines.length <= 1) return -1;
    if (initialEventId) {
      const active = timelines[activeTimelineIndex];
      if (!active || timelineContainsEvent(initialEventId, active)) return -1;
      return findTimelineIndexForEvent(initialEventId, timelines);
    }
    if (initialMessageId && !resolvedLocal && !resolvedRoot) {
      return findTimelineIndexForMessage(initialMessageId, timelines);
    }
    return -1;
  }, [
    initialEventId,
    initialMessageId,
    resolvedLocal,
    resolvedRoot,
    timelines,
    activeTimelineIndex,
  ]);

  // Side-effect: switch swimlane selection when resolution comes from root
  // (i.e. requires moving the user to a different row to see the resolved
  // event). Calls `timelineState.select` with `{ preserveDeepLink: true }`
  // so hosts know to keep the URL `?message=` / `?event=` params intact
  // rather than clearing them as they would for a user row click — the
  // imperative scroll about to fire still needs the deep-link target.
  //
  // Gated on `initialMessageId` actually changing — selection-only changes
  // (user clicked a swimlane row) cause `resolvedRoot` to recompute against
  // the still-stale URL message during the intermediate render before URL
  // clearing is applied; firing the side effect there would override the
  // user's just-expressed selection intent.
  const prevMessageIdRef = useRef<string | null | undefined>(undefined);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (prevMessageIdRef.current === initialMessageId) return;
    // A cross-timeline switch is pending: don't consume the message id yet —
    // this effect must re-evaluate after the switch lands and resolution
    // re-runs against the new root.
    if (deepLinkTimelineIndex >= 0) return;
    prevMessageIdRef.current = initialMessageId;
    if (!resolvedRoot) return;
    let targetKey: string | null = null;
    if (resolvedRoot.branchRowKey) {
      targetKey = resolvedRoot.branchRowKey;
    } else if (resolvedRoot.agentSpanId) {
      targetKey = spanSelectKeys.get(resolvedRoot.agentSpanId)?.key ?? null;
    }
    if (timelineState.selected === targetKey) return;
    timelineState.select(targetKey, { preserveDeepLink: true });
  }, [
    initialMessageId,
    deepLinkTimelineIndex,
    resolvedRoot,
    spanSelectKeys,
    timelineState,
  ]);

  // Fire the timeline switch once per deep-link change — a stale `?event=` /
  // `?message=` param left in the URL must not snap the user back after they
  // manually switch timelines away.
  //
  // Mark the key "consumed" only once we've actually switched. While the
  // target isn't found yet (index -1 — e.g. timeline data still building or
  // events still streaming), leave it unconsumed so a later data update with
  // the same key can still trigger the switch. The snap-back guard still
  // holds: after a switch, the target resolves in the now-active timeline,
  // so `deepLinkTimelineIndex` drops to -1 and the consumed key blocks any
  // re-switch even if the user navigates timelines manually.
  const prevDeepLinkRef = useRef<string | null>(null);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (timelines.length <= 1) return;
    const key = initialEventId ?? initialMessageId ?? null;
    if (key === null) {
      prevDeepLinkRef.current = null;
      return;
    }
    if (prevDeepLinkRef.current === key) return;
    if (deepLinkTimelineIndex < 0) return;
    prevDeepLinkRef.current = key;
    if (deepLinkTimelineIndex === activeTimelineIndex) return;
    setActiveTimeline(deepLinkTimelineIndex, { preserveDeepLink: true });
  }, [
    initialEventId,
    initialMessageId,
    deepLinkTimelineIndex,
    activeTimelineIndex,
    setActiveTimeline,
    timelines.length,
  ]);

  // Event deep links targeting a non-visible swimlane lane: with swimlanes
  // on, the event list only contains the selected rows' events, so a target
  // in another agent lane (or branch) is unreachable until that row is
  // selected. The event-id analogue of the message side-effect above.
  const resolvedEventSpan = useMemo(() => {
    if (!initialEventId || !showSwimlanes) return undefined;
    const present = nodeFeedEvents.some(
      (e) => (e as { uuid?: string | null }).uuid === initialEventId
    );
    if (present) return undefined;
    return (
      resolveEventToSpan(initialEventId, timelineData.root) ??
      resolveEventInBranches(initialEventId, timelineData.root)
    );
  }, [initialEventId, showSwimlanes, nodeFeedEvents, timelineData.root]);

  const prevEventIdRef = useRef<string | null | undefined>(undefined);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (prevEventIdRef.current === initialEventId) return;
    // A cross-timeline switch is pending: re-evaluate after it lands.
    if (deepLinkTimelineIndex >= 0) return;
    // Return without advancing prevEventIdRef: flipping the toggle recomputes
    // spanSelectKeys, which re-runs this effect to finish the selection.
    if (
      !includeUtility &&
      resolvedEventSpan?.agentSpanId &&
      !resolvedEventSpan.branchRowKey &&
      !spanSelectKeys.has(resolvedEventSpan.agentSpanId)
    ) {
      setIncludeUtility(true);
      return;
    }
    prevEventIdRef.current = initialEventId;
    if (!resolvedEventSpan) return;
    let targetKey: string | null = null;
    if (resolvedEventSpan.branchRowKey) {
      targetKey = resolvedEventSpan.branchRowKey;
    } else if (resolvedEventSpan.agentSpanId) {
      targetKey =
        spanSelectKeys.get(resolvedEventSpan.agentSpanId)?.key ?? null;
    }
    if (!targetKey) return;
    if (timelineState.selected === targetKey) return;
    timelineState.select(targetKey, { preserveDeepLink: true });
  }, [
    initialEventId,
    deepLinkTimelineIndex,
    resolvedEventSpan,
    spanSelectKeys,
    timelineState,
    includeUtility,
    setIncludeUtility,
  ]);

  const effectiveInitialEventId =
    initialEventId ?? resolved?.eventId ?? branchScrollTarget ?? null;

  // Suppress headroom (swimlane collapse/expand) during programmatic scrolls
  // — fires for any change to the effective scroll target (URL `?event=`,
  // resolved message, branch switch). The reset-anchor uses a debounced
  // lock that stays active while the imperative scroll's retry loop keeps
  // emitting scroll events, so the swimlane doesn't flicker open/closed
  // during the multi-pass settling.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (effectiveInitialEventId) {
      onHeadroomResetAnchor?.(true);
    }
  }, [effectiveInitialEventId, onHeadroomResetAnchor]);

  return { effectiveInitialEventId };
}
